import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@repo/ui/lib/utils";
import { IconArrowRight } from "central-icons/IconArrowRight";
import type { IconComponent } from "@repo/ui/types/icon";

const buttonVariants = cva(
  `inline-flex
  items-center
  justify-center
  gap-1.5
  whitespace-nowrap
  text-sm
  font-medium
  select-none
  transition-colors
  focus-visible:outline-none
  focus-visible:shadow-focus
  disabled:pointer-events-none
  disabled:opacity-50
  [&_svg]:pointer-events-none
  [&_svg:not([class*='size-'])]:size-4
  [&_svg]:shrink-0`,
  {
    variants: {
      variant: {
        unstyled: `
          bg-transparent
          hover:bg-transparent
          font-normal
          border-none
          shadow-none
          appearance-none
          inline-block
          rounded-none
        `,

        default: `
          bg-button-primary/95
          text-button-primary-foreground
          hover:bg-button-primary
          active:bg-button-primary
        `,

        destructive: `
          bg-button-destructive
          text-button-destructive-foreground
          hover:bg-button-destructive-active
          active:bg-button-destructive-active
        `,

        secondary: `
          bg-button-secondary
          text-button-secondary-foreground
          hover:bg-button-secondary-active
          active:bg-button-secondary-active
          shadow-sm
        `,

        outline: `
          border
          text-button-outline-foreground
          hover:bg-button-outline-active
          active:bg-button-outline-active
        `,

        "destructive-outline": `
          border
          text-button-destructive-outline-foreground
          hover:bg-button-outline-active
          active:bg-button-outline-active
        `,

        ghost: `
          text-button-ghost-foreground
          hover:bg-button-ghost-active
          active:bg-button-ghost-active
          focus-visible:bg-background
        `,

        filter: `
          border
          bg-button-secondary
          text-button-filter-foreground
          hover:bg-button-filter-active
          active:bg-button-filter-active
          focus-visible:bg-button-filter-active
        `,

        link: `
          text-foreground
          underline-offset-4
          hover:underline
        `,
      },
      size: {
        default: "h-9 px-3",
        unstyled: "px-0 gap-0 text-inherit",
        xs: "h-6 px-2 text-xs [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 px-2",
        lg: "h-12 px-3",
        xlg: "h-14 px-10",
        icon: "h-8 w-8",
        filter: "h-9 px-2 gap-1.5 ",
        "filter-sm": "h-7 px-2 gap-1",
      },
      shape: {
        default: "rounded-lg",
        rounded: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shape, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, shape, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

const UnstyledButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, ...props }, ref) => {
    return (
      <Button
        variant="unstyled"
        size="unstyled"
        ref={ref}
        className={className}
        {...props}
      />
    );
  }
);
UnstyledButton.displayName = "UnstyledButton";

export interface LinkButtonProps extends ButtonProps {
  linkIcon?: IconComponent;
}

const LinkButton = React.forwardRef<HTMLButtonElement, LinkButtonProps>(
  ({ className, linkIcon = IconArrowRight, children, ...props }, ref) => {
    const LinkIcon = linkIcon;
    return (
      <Button
        ref={ref}
        className={cn(className, "underline")}
        variant="link"
        size="xs"
        asChild
        {...props}
      >
        <div className="group inline-flex items-center p-0">
          {children}
          {LinkIcon && (
            <LinkIcon
              size={16}
              className="group-hover:translate-x-1 group-focus:translate-x-1 transition-transform duration-200"
            />
          )}
        </div>
      </Button>
    );
  }
);
LinkButton.displayName = "LinkButton";

export { Button, buttonVariants, UnstyledButton, LinkButton };
